import * as THREE from "three";
import {
  children,
  copy,
  interactions,
  camera,
  animate,
  session,
  $,
  cursors,
  list,
  XREventMap,
} from "@apendua/freedom";
import {
  just,
  on,
  effects,
  set,
  use,
  effectMap,
  component,
  makeNonReactive,
  onCleanup,
  Signal,
} from "@apendua/signal";
import { interval, switchMap, tap, BehaviorSubject } from "rxjs";
import { observeFn } from "@apendua/signal";
import { createSphere } from "@apendua.dev/polytope";
import { createClicks } from "@apendua.dev/sound";
import Puzzle, { PuzzleOptions } from "../store/Puzzle";
import PuzzleGroup from "../store/PuzzleGroup";
import { createGradientTexture } from "../elements/environment";
import { cursorMaterial, pieceMaterial, sphereGeometry } from "../elements/material";
import StatusUI from "../views/StatusUI";
import { createMusicControls, Music } from "@apendua.dev/sound/xr";
import { createDiagnostics, Diagnostics } from "@apendua.dev/diagnostics";
import glass_desk_habits from "@apendua.dev/media/glass_desk_habits.mp3";

/** How far a piece is thrown from the centre, in polytope radii. */
const SCATTER = 1.4;

/** How much of the highlight material shows while a cluster is pointed at. */
const HIGHLIGHT = 0.5;

const Game = component((ctx: WeakKey) => {
  const scale = 0.13;

  const query = new URLSearchParams(location.search);

  // Lit by a sky/ground gradient rather than by lights alone: the pieces are
  // faceted, and a graded surround is what makes neighbouring facets of the
  // same colour read as separate planes instead of one flat patch.
  const environment = createGradientTexture("#1a2136", "#38466a", "#7186b8");
  const backdrop = createGradientTexture("#080c16", "#141d33", "#28395f");

  const listener = new THREE.AudioListener();
  // Well under the click of a piece snapping home, which is the sound that
  // matters here.
  const music = createMusicControls({ track: glass_desk_habits, volume: 0.3 });
  const diagnostics = createDiagnostics();

  // The puzzle is a 26 cm sphere at arm's length rather than a board, so the
  // click starts falling off nearer than it does in the brick games.
  const clicks = createClicks(listener, { refDistance: 0.1 });
  const sphere = createSphere({ nFaces: 16 });
  const puzzleOptions = new BehaviorSubject<Partial<PuzzleOptions>>({
    nPieces: 1,
    nColors: 6,
    // Matches how main.ts reads the flag, so `?emulator` with no value tunes
    // the puzzle for the emulator rather than only installing it.
    maxMergeDistance: query.has("emulator") ? 1 : 0.1,
    allowRotations: !query.has("emulator"),
    // Close enough that everything stays inside arm's reach, and clear of the
    // panel parked off to the left.
    maxTranslationDistance: SCATTER,
  });
  const puzzle = new Puzzle(sphere, puzzleOptions.getValue());
  // Nudged off centre, so the panel parked to the left has somewhere to sit
  // that a scattered piece will not drift across.
  const position = new BehaviorSubject(new THREE.Vector3(0.09, 0, -0.8));

  const elapsed = Signal.of(0);
  let startedAt = performance.now();
  let solvedAt: number | null = null;

  /**
   * Deal a puzzle. `delta` moves the difficulty: zero re-cuts the same number
   * of pieces, one takes the sphere one piece further, and passing an absolute
   * count is how the panel gets back to the beginning.
   */
  const deal = makeNonReactive((delta: number, absolute?: number) => {
    const options = puzzleOptions.getValue();
    const nPieces = Math.max(1, absolute ?? (options.nPieces ?? 1) + delta);
    puzzleOptions.next({ ...options, nPieces });
    puzzle.init(puzzleOptions.getValue());
    startedAt = performance.now();
    solvedAt = null;
    elapsed.set(0);
  });

  /**
   * Let go of a cluster where it might belong. Reports whether it snapped, so
   * the caller can make a noise about it, and stops the clock on the solve.
   */
  const drop = makeNonReactive((group: PuzzleGroup) => {
    if (!puzzle.drop(group)) {
      return false;
    }
    if (puzzle.solved && solvedAt === null) {
      solvedAt = performance.now();
    }
    return true;
  });

  const { makeInteractive } = interactions(ctx);
  // An additive glow rather than the package default ball, so the dot stays
  // readable wherever it lands on the polytope.
  const cursor = new THREE.Mesh(sphereGeometry(0.006), cursorMaterial("#8fd3ff"));

  return just(new THREE.Scene(), [
    effects((scene) => {
      scene.environment = environment;
      scene.background = backdrop;
    }),
    // In a passthrough AR session the real room is the background, so
    // nothing may be painted behind the puzzle for as long as the session
    // lasts. Outside one — including the immersive-vr fallback used by
    // headsets with no passthrough camera, whose sessions are opaque — the
    // gradient is all there is to sit the polytope against, so it should
    // stay.
    effectMap((scene) => {
      return session(ctx).pipe(
        switchMap((xrSession$) => {
          return xrSession$.pipe(
            effects((xrSession) => {
              if (xrSession.environmentBlendMode !== "opaque") {
                scene.background = null;
                onCleanup(() => {
                  scene.background = backdrop;
                });
              }
            }),
          );
        }),
      );
    }),
    effects(() => {
      use(camera(ctx).pipe(children($(listener))));
    }),
    // The frame rate readout rides the head as well: a panel parked in the room
    // is one the player has to go and find.
    effects(() => {
      use(Diagnostics(ctx, diagnostics));
    }),
    // The clock is read once every few frames rather than every one: it is
    // shown to the second, and `Signal` drops a write that does not change the
    // value, so the panel only re-renders when the digits actually move.
    effects(() => {
      use(
        interval(200).pipe(
          tap(() => {
            elapsed.set(Math.floor(((solvedAt ?? performance.now()) - startedAt) / 1000));
          }),
        ),
      );
    }),
    children(
      $(new THREE.HemisphereLight(0xdce8ff, 0x39435c, 1.4)),
      $(new THREE.DirectionalLight(0xffffff, 1.7), [
        effects((o) => {
          o.position.set(1.5, 2.5, 1.5);
        }),
      ]),
      // A cool counter-light from behind and below, so a piece turned away from
      // the key light keeps an edge instead of going flat black.
      $(new THREE.DirectionalLight(0x9fc4ff, 0.7), [
        effects((o) => {
          o.position.set(-1.5, -1, -2);
        }),
      ]),
      // Fill from where the player is. Half of what is on screen at any moment
      // is a cut face pointing more or less back at them, and without this
      // those go to a flat grey that gives away nothing about the shape.
      $(new THREE.DirectionalLight(0xffffff, 0.8), [
        effects((o) => {
          o.position.set(0.2, 0.4, 1);
        }),
      ]),
    ),
    children(
      $(new THREE.Group<XREventMap>(), [
        tap((o) => {
          o.scale.set(scale, scale, scale);
        }),
        copy("position", position),
        children(
          Music(ctx, listener, music),
          // Alongside the clusters rather than inside one, so a click outlives
          // the cluster that earned it: a merge deletes the group it was
          // dropped from.
          $(clicks.object),
          list(observeFn(puzzle.groups).values(), (puzzleGroup) => {
            const hovered = Signal.of(false);
            let grabbing = false;
            let dragging = false;
            return $(puzzleGroup.object, [
              children(
                list(observeFn(puzzleGroup.pieces).values(), (key) => {
                  return $(new THREE.Mesh<THREE.BufferGeometry, THREE.Material, XREventMap>(), [
                    set("geometry", observeFn(puzzle).getGeometry(key, false)),
                    set("material", () => pieceMaterial(hovered.get() ? HIGHLIGHT : 0)),
                  ]);
                }),
              ),
              makeInteractive(),
              effectMap((o) => {
                return animate(ctx, () => {
                  let targetPosition = new THREE.Vector3();
                  if (grabbing) {
                    targetPosition
                      .copy(puzzleGroup.center)
                      .applyMatrix4(o.matrix)
                      .sub(o.position)
                      .multiplyScalar(-1);
                  } else if (puzzle.groups.size === 1 && !dragging) {
                    targetPosition.set(0, 0, 0);
                  } else {
                    targetPosition.copy(o.position);
                  }
                  if (grabbing) {
                    const distance = targetPosition.distanceTo(o.position);
                    const minDistance = 0.1;
                    if (distance > minDistance) {
                      o.position.lerp(targetPosition, 0.5);
                    }
                  } else {
                    const distance = targetPosition.distanceTo(o.position);
                    if (distance > 0) {
                      o.position.lerp(targetPosition, 0.5);
                    }
                  }
                  if (puzzle.groups.size === 1 && !grabbing && !dragging) {
                    o.rotation.y += 0.005;
                    o.rotation.x += 0.01;
                  }
                });
              }),
              on("pointerenter", () => {
                hovered.set(true);
              }),
              on("pointerleave", () => {
                hovered.set(false);
              }),
              on("selectstart", (event) => {
                if (puzzle.groups.size === 1) {
                  deal(1);
                } else {
                  if (!event.target.userData.oldParent) {
                    event.target.userData.oldParent = event.target.parent;
                  }
                  event.controller.attach(event.target);
                  dragging = true;
                }
              }),
              on("selectend", (event) => {
                if (event.target.userData.oldParent && event.target.parent === event.controller) {
                  event.target.userData.oldParent.attach(event.target);
                  event.target.userData.oldParent = null;
                  if (drop(puzzleGroup)) {
                    clicks.play(puzzleGroup.object.position);
                    event.inputSource.gamepad?.hapticActuators[0]
                      ?.playEffect("trigger-rumble", {
                        duration: 50,
                        strongMagnitude: 1,
                        weakMagnitude: 0.5,
                      })
                      ?.catch(() => {});
                  }
                  dragging = false;
                }
              }),
              on("squeezestart", (event) => {
                if (!event.target.userData.oldParent) {
                  event.target.userData.oldParent = event.target.parent;
                }
                event.controller.attach(event.target);
                grabbing = true;
              }),
              on("squeezeend", (event) => {
                if (event.target.userData.oldParent && event.target.parent === event.controller) {
                  event.target.userData.oldParent.attach(event.target);
                  event.target.userData.oldParent = null;
                  if (drop(puzzleGroup)) {
                    clicks.play(puzzleGroup.object.position);
                  }
                }
                grabbing = false;
              }),
            ]);
          }),
        ),
      ]),
      StatusUI(ctx, music.output, diagnostics, [
        effects((ui) => {
          ui.pieces = puzzle.nPieces;
          ui.clusters = puzzle.nGroups;
          ui.solved = puzzle.solved;
          ui.seconds = elapsed.get();
          ui.volume = music.volume.get();
        }),
        set({
          // Off to the player's left, turned in to face them, and far enough
          // out that a scattered piece never lands on top of it.
          position: new THREE.Vector3(-0.45, -0.02, -0.62),
          rotation: new THREE.Euler(0, Math.PI / 6, 0),
        }),
        on("shuffle", () => deal(0)),
        on("addPiece", () => deal(1)),
        on("startOver", () => deal(0, 1)),
        on("quieter", () => music.step(-1)),
        on("louder", () => music.step(1)),
      ]),
      cursors(ctx, { object: cursor }),
    ),
  ]);
});

export default Game;
